import { Box } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC, type ReactNode, useEffect, useRef } from 'react';
import { liquidGlassPillShadowSx, liquidGlassSurfaceFillSx } from '../helpers/liquidGlassSurface';

export type SegmentedChipItem = {
    id: string;
    label: string;
    icon?: ReactNode;
};

type SegmentedChipBarProps = {
    items: SegmentedChipItem[];
    activeId: string;
    onChange: (id: string) => void;
    preserveFocusOnClick?: boolean;
};

export const SegmentedChipBar: FC<SegmentedChipBarProps> = ({ items, activeId, onChange, preserveFocusOnClick = false }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemElsRef = useRef<Record<string, HTMLButtonElement | null>>({});

    useEffect(() => {
        const container = containerRef.current;
        const item = itemElsRef.current[activeId];
        if (!container || !item) return;
        const delta = item.getBoundingClientRect().left - container.getBoundingClientRect().left;
        if (Math.abs(delta) >= 1) container.scrollLeft += delta;
    }, [activeId, items]);

    return <Box sx={{ borderRadius: '999px', overflow: 'visible', ...liquidGlassPillShadowSx }}>
        <Box ref={containerRef} sx={{ display: 'flex', gap: '2px', p: '3px', overflowX: 'auto', scrollbarWidth: 'none', borderRadius: '999px', ...liquidGlassSurfaceFillSx, '&::-webkit-scrollbar': { display: 'none' } }}>
            {items.map(item => {
                const selected = item.id === activeId;
                return <Box component="button" type="button" key={item.id} data-selected={selected} ref={element => { itemElsRef.current[item.id] = element as HTMLButtonElement | null; }} onClick={() => onChange(item.id)} onMouseDown={preserveFocusOnClick ? event => event.preventDefault() : undefined} sx={{ display: 'inline-flex', flexShrink: 0, alignItems: 'center', gap: '6px', height: 32, px: '14px', m: 0, border: 0, borderRadius: '999px', cursor: 'pointer', whiteSpace: 'nowrap', bgcolor: selected ? 'color-mix(in srgb, var(--tg-theme-text-color) 12%, transparent)' : 'transparent', appearance: 'none', WebkitAppearance: 'none', WebkitTapHighlightColor: 'transparent' }}>
                    {item.icon}
                    <Text style={{ fontSize: 14, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', color: selected ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)' }}>{item.label}</Text>
                </Box>;
            })}
        </Box>
    </Box>;
};
